import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { AfregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/afregning-dto';
import { OpfoelgningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-dto';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { EnvironmentLoaderService } from './environment-loader.service';
import { FejlDashboardSummaryDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/fejl-dashboard-summary-dto';
import { ExcelDocumentUploadResponseDto } from '../api/models/OK/Egis/Service/Models/DTO/excel-document-upload-response-dto';
import { UpdateServiceabonnementResponseDto } from '../api/models/OK/Egis/Service/Models/Response/update-serviceabonnement-response-dto';
import { ServiceaftalePricecacheUpdateDto } from '../api/models/OK/Egis/Service/Models/Response/serviceaftale-pricecache-update-dto';
import { OAuthService } from 'angular-oauth2-oidc';

export interface OpfoelgningChanged {
  entityNo: number;
  opfoelgning?: OpfoelgningDto;
}

export interface ExcelDocumentDownloadResponseDto {
  documentId: string;
  limit: number;
}

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  TEST_EVENT: string = 'test';
  SERVICEORDRE_CREATED_EVENT: string = 'serviceordre-created';
  SERVICEORDRE_CHANGED_EVENT: string = 'serviceordre-changed';
  SERVICAFTALE_CREATED_EVENT: string = 'serviceaftale-created';
  SERVICAFTALE_CHANGED_EVENT: string = 'serviceaftale-changed';
  OPFOELGNING_CHANGED_EVENT: string = 'opfoelgning-changed';
  SERVICEAFREGNING_CREATED_EVENT: string = 'serviceafregning-created';
  EXCEL_CREATED_EVENT: string = 'excel-created';
  EXCEL_CREATE_FAILED_EVENT: string = 'excel-create-failed';
  EXCEL_UPLOADED_EVENT: string = 'excel-uploaded';
  EXCEL_UPLOAD_FAILED_EVENT: string = 'excel-upload-failed';
  FEJLDASHBOARD_SUMMARY_EVENT: string = 'fejldashboard-summary';
  UPDATESERVICEABONNEMENT_UPDATED_EVENT: string = 'serviceabonnement-updated';
  UPDATESERVICEABONNEMENT_FAILED_EVENT: string = 'serviceabonnement-update-failed';
  SERVICEORDRE_CANCELLED_EVENT: string = 'serviceordre-cancelled';
  SERVICEORDRE_CANCEL_FAILED_EVENT: string = 'serviceordre-cancel-failed';
  SERVICEORDRE_REJECTED_EVENT: string = 'serviceordre-rejected';
  SERVICEORDRE_REJECTION_FAILED_EVENT: string = 'serviceordre-rejection-failed';
  SERVICEAFTALEPRICECACHE_UPDATED_EVENT: string = 'serviceaftale-price-cache-updated';
  SERVICEAFTALEPRICECACHE_FAILED_EVENT: string = 'serviceaftale-price-cache-update-failed';

  baseUrl: string;

  private hubConnection: signalR.HubConnection;

  serviceordreCreated$ = new Subject<ServiceordreDto>();
  serviceordreChanged$ = new Subject<ServiceordreDto>();
  serviceaftaleCreated$ = new Subject<ServiceaftaleDto>();
  serviceaftaleChanged$ = new Subject<ServiceaftaleDto>();
  opfoelgningChanged$ = new Subject<OpfoelgningChanged>();
  serviceafregningCreated$ = new Subject<AfregningDto>();
  excelCreated$ = new Subject<ExcelDocumentDownloadResponseDto>();
  excelCreateFailed$ = new Subject<string>();
  excelUploaded$ = new Subject<ExcelDocumentUploadResponseDto>();
  excelUploadFailed$ = new Subject<string>();
  fejlDashboardSummary$ = new Subject<FejlDashboardSummaryDto>();
  updateServiceabonnementUpdated$ = new Subject<UpdateServiceabonnementResponseDto>();
  updateServiceabonnementFailed$ = new Subject<string>();
  serviceordreCancelled$ = new Subject<string>();
  serviceordreCancelFailed$ = new Subject<string>();
  serviceordreRejected$ = new Subject<string>();
  serviceordreRejectionFailed$ = new Subject<string>();
  serviceaftalePriceCacheUpdated$ = new Subject<ServiceaftalePricecacheUpdateDto>();
  serviceaftalePriceCacheFailed$ = new Subject<string>();

  isConnected$ = new Subject<boolean>();

  constructor(private environmentLoaderService: EnvironmentLoaderService, private oauthService: OAuthService) {
    this.baseUrl = this.environmentLoaderService.config.egisServiceBaseUrl;
  }

  startConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.baseUrl + '/hub', {
        accessTokenFactory: () => this.oauthService.getAccessToken(),
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection
      .start()
      .then(() => {
        console.log('Connection started');
        this.isConnected$.next(true);
      })
      .catch((err) => {
        console.log('Error while starting connection: ' + err);
        this.isConnected$.next(false);
      });

    this.hubConnection.onreconnected(() => {
      this.isConnected$.next(true);
    });

    this.hubConnection.onreconnecting(() => {
      this.isConnected$.next(false);
    });
  };

  serviceordreCreatedListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_CREATED_EVENT, (serviceordre: ServiceordreDto) => {
      this.serviceordreCreated$.next(serviceordre);
    });
  };

  serviceordreChangedListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_CHANGED_EVENT, (serviceordre: ServiceordreDto) => {
      this.serviceordreChanged$.next(serviceordre);
    });
  };

  serviceaftaleCreatedListener = () => {
    this.hubConnection.on(this.SERVICAFTALE_CREATED_EVENT, (serviceaftale: ServiceaftaleDto) => {
      this.serviceaftaleCreated$.next(serviceaftale);
    });
  };

  serviceaftaleChangedListener = () => {
    this.hubConnection.on(this.SERVICAFTALE_CHANGED_EVENT, (serviceaftale: ServiceaftaleDto) => {
      this.serviceaftaleChanged$.next(serviceaftale);
    });
  };

  opfoelgningChangedListener = () => {
    this.hubConnection.on(this.OPFOELGNING_CHANGED_EVENT, (entityNo: number, opfoelgning: OpfoelgningDto) => {
      this.opfoelgningChanged$.next({
        entityNo: entityNo,
        opfoelgning: opfoelgning,
      });
    });
  };

  serviceafregningCreatedListener = () => {
    this.hubConnection.on(this.SERVICEAFREGNING_CREATED_EVENT, (afregning: AfregningDto) => {
      this.serviceafregningCreated$.next(afregning);
    });
  };

  excelCreatedListener = () => {
    this.hubConnection.on(this.EXCEL_CREATED_EVENT, (response: ExcelDocumentDownloadResponseDto) => {
      this.excelCreated$.next(response);
    });
  };

  excelCreateFailedListener = () => {
    this.hubConnection.on(this.EXCEL_CREATE_FAILED_EVENT, (message: string) => {
      this.excelCreateFailed$.next(message);
    });
  };

  excelUploadedListener = () => {
    this.hubConnection.on(this.EXCEL_UPLOADED_EVENT, (reponse: ExcelDocumentUploadResponseDto) => {
      this.excelUploaded$.next(reponse);
    });
  };

  excelUploadFailedListener = () => {
    this.hubConnection.on(this.EXCEL_UPLOAD_FAILED_EVENT, (message: string) => {
      this.excelUploadFailed$.next(message);
    });
  };

  fejlDashboardSummaryListener = () => {
    this.hubConnection.on(this.FEJLDASHBOARD_SUMMARY_EVENT, (message: FejlDashboardSummaryDto) => {
      this.fejlDashboardSummary$.next(message);
    });
  };

  updateServiceabonnementUpdatedListener = () => {
    this.hubConnection.on(this.UPDATESERVICEABONNEMENT_UPDATED_EVENT, (message: UpdateServiceabonnementResponseDto) => {
      this.updateServiceabonnementUpdated$.next(message);
    });
  };

  updateServiceabonnementFailedListener = () => {
    this.hubConnection.on(this.UPDATESERVICEABONNEMENT_FAILED_EVENT, (message: string) => {
      this.updateServiceabonnementFailed$.next(message);
    });
  };

  serviceordreCancelledListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_CANCELLED_EVENT, (message: string) => {
      this.serviceordreCancelled$.next(message);
    });
  };

  serviceordreCancelFailedListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_CANCEL_FAILED_EVENT, (message: string) => {
      this.serviceordreCancelFailed$.next(message);
    });
  };

  serviceordreRejectedListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_REJECTED_EVENT, (message: string) => {
      this.serviceordreRejected$.next(message);
    });
  };

  serviceordreRejectionFailedListener = () => {
    this.hubConnection.on(this.SERVICEORDRE_REJECTION_FAILED_EVENT, (message: string) => {
      this.serviceordreRejectionFailed$.next(message);
    });
  };

  serviceaftalePriceCacheUpdatedListener = () => {
    this.hubConnection.on(this.SERVICEAFTALEPRICECACHE_UPDATED_EVENT, (message: ServiceaftalePricecacheUpdateDto) => {
      this.serviceaftalePriceCacheUpdated$.next(message);
    });
  };

  serviceaftalePriceCacheFailedListener = () => {
    this.hubConnection.on(this.SERVICEAFTALEPRICECACHE_FAILED_EVENT, (message: string) => {
      this.serviceaftalePriceCacheFailed$.next(message);
    });
  };
}
